import InfiniteScroll from 'react-infinite-scroll-component';
import { css, styled } from 'goober';

import { theme } from '@/theme/setupTheme';
import { useVisibleTableData } from '@/ui/table/helpers/use-visible-data';
import type { TableSkeletonConfig } from '@/ui/table/infinite-table/table-body';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import type { IHeaderRow, Row, ISort } from '@/ui/table/table.types';
import { useCallback } from 'react';
import type { FetchNextPageOptions } from '@tanstack/react-query';
import type { PrimitiveAtom } from 'jotai';
import { useAtom } from 'jotai';

interface Props<T, Sort extends string = string> {
  bodyShadow?: boolean;
  borderColor?: string;
  data: T[];
  dataTestId?: string;
  hasMore: boolean;
  headers: IHeaderRow<Sort>;
  height: string;
  id?: string;
  isFetchingNextPage?: boolean;
  isLoading: boolean;
  onLoadMore?: (options?: FetchNextPageOptions) => Promise<unknown>;
  onSort?: (sort: ISort<Sort>) => void;
  rows: Row[];
  scrollableTarget?: string;
  skeletonConfig?: Partial<TableSkeletonConfig>;
  sortState: PrimitiveAtom<ISort<Sort>>;
  tableBorderRadius?: string;
  tableLayout?: 'auto' | 'fixed';
  tableShadow?: boolean;
}

export const InfiniteScrollTable = <T, Sort extends string = string>({
  bodyShadow,
  borderColor,
  data,
  dataTestId,
  hasMore,
  headers,
  height,
  id,
  isFetchingNextPage = false,
  isLoading,
  onLoadMore,
  onSort,
  rows,
  scrollableTarget,
  skeletonConfig,
  sortState,
  tableBorderRadius,
  tableLayout,
  tableShadow,
}: Props<T, Sort>) => {
  const [sortData, setSortData] = useAtom(sortState);
  const { visibleColumns, visibleRows } = useVisibleTableData<Sort>({
    rows,
    header: headers,
  });

  const handleSort = (sort: ISort<Sort>) => {
    if (isLoading) return; // prevent multisorting while previously loading
    setSortData(sort);
    onSort?.(sort);
  };

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || !onLoadMore || isFetchingNextPage) {
      return;
    }

    await onLoadMore();
  }, [hasMore, isFetchingNextPage, onLoadMore]);

  return (
    <InfiniteScroll
      dataLength={data.length}
      next={handleLoadMore}
      hasMore={hasMore}
      loader={null}
      height={scrollableTarget ? undefined : height}
      scrollableTarget={scrollableTarget}
      className={ScrollClassName}
      style={scrollableTarget ? { overflow: 'visible' } : undefined}
    >
      <Table
        id={id}
        data-testid={dataTestId || `${id}-table`}
        radius={tableBorderRadius}
        shadow={tableShadow}
        tableLayout={tableLayout}
      >
        <StickyThead data-testid={`${id}-thead`}>
          <tr>
            {visibleColumns.map(column => (
              <TableHeaderCell<Sort>
                key={column.columnKey}
                cell={column}
                sort={
                  sortData ? { ...sortData, onSort: handleSort } : undefined
                }
              />
            ))}
          </tr>
        </StickyThead>
        <TableBody
          id={id ? `${id}-tbody` : undefined}
          rows={visibleRows}
          isLoading={isLoading}
          borderColor={borderColor}
          skeletonConfig={{
            columns: headers.cells.length,
            rowConfig: headers.cells.map(({ align }) => ({ align })),
            ...skeletonConfig,
          }}
          shadow={bodyShadow}
        />
      </Table>
    </InfiniteScroll>
  );
};

const Table = styled('table')<{
  radius?: string;
  shadow?: boolean;
  tableLayout?: 'auto' | 'fixed';
}>`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-collapse: separate;
  border-radius: ${({ radius }) => radius ?? '12px'};
  border-spacing: 0;
  box-shadow: ${({ shadow, theme }) => (shadow ? theme.shadow.table : 'none')};
  table-layout: ${({ tableLayout }) => tableLayout ?? 'auto'};
  width: calc(100% - 15px);
`;

const StickyThead = styled('thead')`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ScrollClassName = css`
  ${theme.mixins.scrollbar}
`;
